import { useCallback, useEffect, useMemo, useState } from 'react'
import s from './styles.module.scss'
import * as api from 'api'
import { Alert, Layouts, Loader, NoData } from 'components'
import { head, getTimeFormats } from 'helpers'

const PER_PAGE = 25

const PRESET_FILTERS = [
  { key: 'all', label: 'All Activity', icon: 'insights', filters: {} },
  { key: 'leads', label: 'Leads', icon: 'touch_app', filters: { eventType: 'lead_interaction' } },
  { key: 'matrimonial', label: 'Matrimonial', icon: 'favorite', filters: { targetType: 'matrimonial_profile' } },
  { key: 'jobs', label: 'Jobs', icon: 'work', filters: { targetType: 'job' } },
  { key: 'resumes', label: 'Resumes', icon: 'description', filters: { targetType: 'resume' } },
  { key: 'business', label: 'Business', icon: 'storefront', filters: { targetType: 'business' } },
  { key: 'spirituality', label: 'Spirituality', icon: 'self_improvement', filters: { targetType: 'spirituality' } },
  { key: 'donation', label: 'Donations', icon: 'volunteer_activism', filters: { targetType: 'donation' } },
  { key: 'contact', label: 'Contact', icon: 'support_agent', filters: { targetType: 'support' } },
]

const DATE_PRESETS = [
  {
    key: 'today',
    label: 'Today',
    getRange: () => {
      const now = new Date()
      const start = new Date(now.getFullYear(), now.getMonth(), now.getDate())
      const end = new Date(start)
      end.setHours(23, 59, 59, 999)
      return { from: formatDate(start), to: formatDate(end) }
    },
  },
  {
    key: '7days',
    label: '7 days',
    getRange: () => {
      const now = new Date()
      const start = new Date(now.getFullYear(), now.getMonth(), now.getDate())
      start.setDate(start.getDate() - 6)
      return { from: formatDate(start), to: formatDate(new Date()) }
    },
  },
  {
    key: '30days',
    label: '30 days',
    getRange: () => {
      const now = new Date()
      const start = new Date(now.getFullYear(), now.getMonth(), now.getDate())
      start.setDate(start.getDate() - 29)
      return { from: formatDate(start), to: formatDate(new Date()) }
    },
  },
  { key: 'custom', label: 'Custom', custom: true },
]

function formatDate(date) {
  return date.toISOString().split('T')[0]
}

function formatShortDate(dateStr) {
  if (!dateStr) return '—'
  const date = new Date(dateStr)
  if (Number.isNaN(date.getTime())) return dateStr
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

function formatHourLabel(hour) {
  const h = Number(hour)
  if (Number.isNaN(h)) return '—'
  if (h === 0) return '12 AM'
  if (h < 12) return `${h} AM`
  if (h === 12) return '12 PM'
  return `${h - 12} PM`
}

export default function ActivityAnalytics() {
  const [presetKey, setPresetKey] = useState('all')
  const [datePresetKey, setDatePresetKey] = useState('7days')
  const [customRange, setCustomRange] = useState({ from: '', to: '' })
  const [selectedUser, setSelectedUser] = useState(null)
  const [userQuery, setUserQuery] = useState('')
  const [userResults, setUserResults] = useState([])
  const [userSearching, setUserSearching] = useState(false)

  const [pageNo, setPageNo] = useState(1)
  const [loading, setLoading] = useState(false)
  const [summary, setSummary] = useState(null)
  const [overview, setOverview] = useState(null)
  const [activities, setActivities] = useState([])
  const [total, setTotal] = useState(0)
  const [uniqueUsers, setUniqueUsers] = useState(0)

  const activePreset = useMemo(
    () => PRESET_FILTERS.find(item => item.key === presetKey) || PRESET_FILTERS[0],
    [presetKey]
  )
  const activeDatePreset = useMemo(
    () => DATE_PRESETS.find(item => item.key === datePresetKey) || DATE_PRESETS[0],
    [datePresetKey]
  )

  const computedFilters = useMemo(() => {
    const filters = { ...activePreset.filters }
    const range =
      datePresetKey === 'custom'
        ? { from: customRange.from || '', to: customRange.to || '' }
        : activeDatePreset.getRange?.() || {}

    if (range.from) filters.dateFrom = range.from
    if (range.to) filters.dateTo = range.to
    if (selectedUser?.userId) filters.fkUserId = selectedUser.userId
    return filters
  }, [activePreset, activeDatePreset, customRange, datePresetKey, selectedUser])

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const params = { ...computedFilters, pageNo, perPage: PER_PAGE }
      const [summaryRes, listRes, overviewRes] = await Promise.all([
        api.admin.analytics.fetchSummary(computedFilters),
        api.admin.analytics.fetchList(params),
        api.admin.analytics.fetchOverview(computedFilters),
      ])

      if (summaryRes?.code === 200) setSummary(summaryRes.payload)
      else if (summaryRes?.message) Alert.error(summaryRes.message)

      if (listRes?.code === 200) {
        setActivities(listRes.payload.activities || [])
        setTotal(listRes.payload.total || 0)
        setUniqueUsers(listRes.payload.uniqueUsers || 0)
      } else if (listRes?.message) Alert.error(listRes.message)

      if (overviewRes?.code === 200) setOverview(overviewRes.payload)
      else if (overviewRes?.message) Alert.error(overviewRes.message)
    } catch (error) {
      console.error('Failed to load activity analytics', error)
      Alert.error('Failed to load activity analytics')
    } finally {
      setLoading(false)
    }
  }, [computedFilters, pageNo])

  useEffect(() => {
    head({ title: 'Activity Analytics | BCN Admin' })
  }, [])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  useEffect(() => {
    setPageNo(1)
  }, [computedFilters])

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / PER_PAGE)), [total])

  const handleDatePresetChange = key => {
    setDatePresetKey(key)
    if (key !== 'custom') setCustomRange({ from: '', to: '' })
  }

  const handleCustomDateChange = event => {
    const { name, value } = event.target
    setCustomRange(prev => ({ ...prev, [name]: value }))
    setDatePresetKey('custom')
  }

  const handleUserLookup = async () => {
    const query = userQuery.trim()
    if (!query) {
      setUserSearching(true)
      try {
        setPageNo(1)
        await fetchData()
      } finally {
        setUserSearching(false)
      }
      return
    }

    setUserSearching(true)
    try {
      const response = await api.admin.analytics.lookupUser({ query })
      if (response?.code === 200) setUserResults(response.payload || [])
      else if (response?.message) Alert.error(response.message)
    } catch (error) {
      console.error('User lookup failed', error)
      Alert.error('Unable to find member. Try a phone number or BCN ID.')
    } finally {
      setUserSearching(false)
    }
  }

  const handleSelectUser = user => {
    setSelectedUser(user)
    setUserResults([])
    setUserQuery('')
  }

  const handleClearUser = () => {
    setSelectedUser(null)
    setUserResults([])
  }

  const filterSummary = useMemo(() => {
    const bits = [activePreset.label, activeDatePreset.label]
    if (datePresetKey === 'custom') {
      bits[1] = customRange.from && customRange.to ? `${customRange.from} → ${customRange.to}` : 'Custom dates'
    }
    if (selectedUser) {
      bits.push(
        `${selectedUser.name}${selectedUser.userRefId ? ` (${selectedUser.userRefId})` : ''}`
      )
    }
    return bits
  }, [activePreset, activeDatePreset, datePresetKey, customRange, selectedUser])

  const trendData = overview?.dailyTrend?.activity || []
  const trendMax = Math.max(...trendData.map(item => item.count), 1)
  const hourlyData = useMemo(() => {
    const buckets = Array.from({ length: 24 }, (_, hour) => ({ hour, count: 0 }))
    ;(overview?.hourlyDistribution || []).forEach(item => {
      const hour = Number(item.hour)
      if (!Number.isNaN(hour) && hour >= 0 && hour < 24) buckets[hour].count = item.count
    })
    return buckets
  }, [overview])
  const hourlyMax = Math.max(...hourlyData.map(item => item.count), 1)

  return (
    <div className={s.page}>
      <Layouts.Classic title="Activity Analytics" />

      <div className={s.hero}>
        <p className={s.heroText}>
          Track member engagement across matrimonial, jobs, business, spirituality, donations, and more.
        </p>
      </div>

      <section className={s.filtersCard}>
        <div className={s.filterBlock}>
          <span className={s.filterLabel}>Module</span>
          <div className={s.chipRow}>
            {PRESET_FILTERS.map(option => (
              <button
                key={option.key}
                type="button"
                className={`${s.chip} ${presetKey === option.key ? s.chipActive : ''}`}
                onClick={() => setPresetKey(option.key)}
              >
                <span className="material-icons-outlined">{option.icon}</span>
                {option.label}
              </button>
            ))}
          </div>
        </div>

        <div className={s.filterBlock}>
          <span className={s.filterLabel}>Date range</span>
          <div className={s.chipRow}>
            {DATE_PRESETS.map(option => (
              <button
                key={option.key}
                type="button"
                className={`${s.chip} ${datePresetKey === option.key ? s.chipActive : ''}`}
                onClick={() => handleDatePresetChange(option.key)}
              >
                {option.label}
              </button>
            ))}
          </div>
          {datePresetKey === 'custom' ? (
            <div className={s.customRange}>
              <label>
                From
                <input name="from" type="date" value={customRange.from} onChange={handleCustomDateChange} />
              </label>
              <label>
                To
                <input name="to" type="date" value={customRange.to} onChange={handleCustomDateChange} />
              </label>
            </div>
          ) : null}
        </div>

        <div className={s.filterBlock}>
          <span className={s.filterLabel}>Member filter</span>
          <div className={s.userSearchRow}>
            <span className="material-icons-outlined">person_search</span>
            <input
              value={userQuery}
              onChange={event => setUserQuery(event.target.value)}
              placeholder="Phone number or BCN ID"
              onKeyDown={event => event.key === 'Enter' && handleUserLookup()}
            />
            <button type="button" className={s.primaryBtn} onClick={handleUserLookup} disabled={userSearching}>
              {userSearching ? 'Searching…' : userQuery.trim() ? 'Find member' : 'Refresh'}
            </button>
            {selectedUser ? (
              <button type="button" className={s.secondaryBtn} onClick={handleClearUser}>
                Clear member
              </button>
            ) : null}
          </div>
          {userResults.length > 0 ? (
            <div className={s.userResults}>
              {userResults.map(result => (
                <button type="button" key={result.userId} onClick={() => handleSelectUser(result)}>
                  <strong>{result.name}</strong>
                  <span>
                    {result.userRefId ? `${result.userRefId} · ` : ''}
                    {result.phoneNo ? `+91-${result.phoneNo}` : ''}
                  </span>
                </button>
              ))}
            </div>
          ) : null}
        </div>

        <div className={s.activeFilters}>
          {filterSummary.map((bit, index) => (
            <span key={index} className={s.filterTag}>
              {bit}
            </span>
          ))}
        </div>
      </section>

      {loading && !summary ? (
        <div className={s.loaderWrap}>
          <Loader color="var(--c-primary)" colorText="var(--c-primary)" />
        </div>
      ) : (
        <>
          <KpiRow
            total={summary?.totalEvents ?? total}
            uniqueUsers={summary?.uniqueUsers ?? uniqueUsers}
            overview={overview}
          />

          {overview?.alerts?.length ? <AlertsStrip alerts={overview.alerts} /> : null}

          <div className={s.dashboardGrid}>
            <section className={s.panel}>
              <div className={s.panelHeader}>
                <h2>7-day activity trend</h2>
                <span className={s.panelMeta}>Events per day</span>
              </div>
              {trendData.length ? (
                <div className={s.trendChart}>
                  {trendData.map(item => (
                    <div key={item.date} className={s.trendBarWrap}>
                      <div className={s.trendBarTrack}>
                        <div
                          className={s.trendBarFill}
                          style={{ height: `${Math.max(8, (item.count / trendMax) * 100)}%` }}
                          title={`${item.count} events`}
                        />
                      </div>
                      <span className={s.trendLabel}>{formatShortDate(item.date)}</span>
                      <span className={s.trendValue}>{item.count}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className={s.panelEmpty}>No trend data for this period.</p>
              )}
            </section>

            <section className={s.panel}>
              <div className={s.panelHeader}>
                <h2>Today by hour</h2>
                <span className={s.panelMeta}>Activity distribution</span>
              </div>
              <div className={s.hourlyChart}>
                {hourlyData.map(item => (
                  <div key={item.hour} className={s.hourBarWrap} title={`${formatHourLabel(item.hour)}: ${item.count}`}>
                    <div
                      className={s.hourBarFill}
                      style={{ height: `${Math.max(4, (item.count / hourlyMax) * 100)}%` }}
                    />
                  </div>
                ))}
              </div>
              <div className={s.hourlyLabels}>
                <span>12 AM</span>
                <span>6 AM</span>
                <span>12 PM</span>
                <span>6 PM</span>
              </div>
            </section>
          </div>

          {overview?.moduleBreakdown?.length ? (
            <section className={s.panel}>
              <div className={s.panelHeader}>
                <h2>Module performance</h2>
                <span className={s.panelMeta}>Today vs yesterday</span>
              </div>
              <div className={s.moduleGrid}>
                {overview.moduleBreakdown.map(module => (
                  <article key={module.key} className={s.moduleCard}>
                    <h3>{module.label}</h3>
                    <div className={s.moduleStats}>
                      <div className={s.moduleStatRow}>
                        <span className={s.moduleStatLabel}>Interactions</span>
                        <div className={s.moduleStatValues}>
                          <strong>{module.interactionsToday}</strong>
                          <em className={module.interactionDelta >= 0 ? s.deltaUp : s.deltaDown}>
                            {module.interactionDelta >= 0 ? '+' : ''}
                            {module.interactionDelta}
                          </em>
                        </div>
                      </div>
                      <div className={s.moduleStatRow}>
                        <span className={s.moduleStatLabel}>Leads</span>
                        <div className={s.moduleStatValues}>
                          <strong>{module.leadsToday}</strong>
                          <em className={module.leadDelta >= 0 ? s.deltaUp : s.deltaDown}>
                            {module.leadDelta >= 0 ? '+' : ''}
                            {module.leadDelta}
                          </em>
                        </div>
                      </div>
                      <div className={s.moduleStatRow}>
                        <span className={s.moduleStatLabel}>Conversion</span>
                        <div className={s.moduleStatValues}>
                          <strong>{module.conversionRate}%</strong>
                        </div>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </section>
          ) : null}

          <div className={s.insightsGrid}>
            <RankedPanel
              title="Top event types"
              subtitle="Most frequent actions"
              items={(summary?.eventsByType || []).slice(0, 6).map(item => ({
                id: item._id,
                label: formatEventLabel(item._id),
                count: item.count,
              }))}
            />
            <RankedPanel
              title="Top targets"
              subtitle="Where activity happens"
              items={(summary?.eventsByTarget || []).slice(0, 6).map(item => ({
                id: item._id,
                label: formatTargetLabel(item._id),
                count: item.count,
              }))}
            />
            <ActiveUsersPanel users={summary?.topUsers || []} />
          </div>

          <section className={s.panel}>
            <div className={s.panelHeader}>
              <h2>Activity log</h2>
              <span className={s.panelMeta}>
                {total} events · page {pageNo} of {totalPages}
              </span>
            </div>
            {loading ? (
              <div className={s.inlineLoader}>
                <Loader color="var(--c-primary)" colorText="var(--c-primary)" />
              </div>
            ) : (
              <>
                <ActivityFeed activities={activities} />
                <PaginationControls pageNo={pageNo} totalPages={totalPages} setPageNo={setPageNo} />
              </>
            )}
          </section>
        </>
      )}
    </div>
  )
}

const KpiRow = ({ total, uniqueUsers, overview }) => {
  const conversion = overview?.conversionSummary?.conversionRate ?? 0
  const leadsToday = overview?.conversionSummary?.totalLeadsToday ?? 0

  const cards = [
    { label: 'Filtered events', value: total, icon: 'bolt', tone: 'primary' },
    { label: 'Unique members', value: uniqueUsers, icon: 'groups', tone: 'blue' },
    { label: 'Events today', value: overview?.eventsToday ?? '—', icon: 'today', tone: 'green' },
    { label: 'Events yesterday', value: overview?.eventsYesterday ?? '—', icon: 'history', tone: 'slate' },
    { label: 'Leads today', value: leadsToday, icon: 'touch_app', tone: 'orange' },
    { label: 'Conversion today', value: `${conversion}%`, icon: 'trending_up', tone: 'purple' },
  ]

  return (
    <section className={s.kpiRow}>
      {cards.map(card => (
        <article key={card.label} className={`${s.kpiCard} ${s[`kpi_${card.tone}`]}`}>
          <span className={`${s.kpiIcon} material-icons-outlined`}>{card.icon}</span>
          <div>
            <span className={s.kpiLabel}>{card.label}</span>
            <strong className={s.kpiValue}>{card.value}</strong>
          </div>
        </article>
      ))}
    </section>
  )
}

const AlertsStrip = ({ alerts }) => (
  <section className={s.alertsStrip}>
    {alerts.map((alert, index) => (
      <div key={index} className={`${s.alertItem} ${s[`alert_${alert.severity}`]}`}>
        <span className="material-icons-outlined">
          {alert.severity === 'error' ? 'error' : alert.severity === 'warning' ? 'warning' : 'info'}
        </span>
        <p>{alert.message}</p>
      </div>
    ))}
  </section>
)

const RankedPanel = ({ title, subtitle, items }) => {
  const max = Math.max(...items.map(item => item.count), 1)

  return (
    <section className={s.panel}>
      <div className={s.panelHeader}>
        <h2>{title}</h2>
        <span className={s.panelMeta}>{subtitle}</span>
      </div>
      {!items.length ? (
        <p className={s.panelEmpty}>No data for selected filters.</p>
      ) : (
        <ul className={s.rankedList}>
          {items.map(item => (
            <li key={item.id || item.label}>
              <div className={s.rankedTop}>
                <span>{item.label}</span>
                <strong>{item.count}</strong>
              </div>
              <div className={s.rankBarTrack}>
                <div className={s.rankBarFill} style={{ width: `${(item.count / max) * 100}%` }} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

const ActiveUsersPanel = ({ users }) => (
  <section className={s.panel}>
    <div className={s.panelHeader}>
      <h2>Most active members</h2>
      <span className={s.panelMeta}>By event count</span>
    </div>
    {!users?.length ? (
      <p className={s.panelEmpty}>No member activity in this view.</p>
    ) : (
      <ul className={s.userRankList}>
        {users.slice(0, 6).map(({ userId, count, user, lastSeenAt }) => {
          const name =
            user?.name || `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || userId || 'Member'
          return (
            <li key={userId || count}>
              <div className={s.userRankAvatar}>
                <span className="material-icons-outlined">person</span>
              </div>
              <div className={s.userRankMeta}>
                <strong>{name}</strong>
                <span>{user?.userRefId ? `BCN ${user.userRefId}` : 'BCN member'}</span>
                <span>{lastSeenAt ? getTimeFormats(new Date(lastSeenAt)) : '—'}</span>
              </div>
              <div className={s.userRankCount}>{count}</div>
            </li>
          )
        })}
      </ul>
    )}
  </section>
)

const ActivityFeed = ({ activities }) => {
  if (!activities?.length) {
    return (
      <div className={s.emptyCard}>
        <NoData />
        <p>No activity recorded for the selected filters.</p>
      </div>
    )
  }

  return (
    <div className={s.feedList}>
      {activities.map(activity => (
        <article key={activity._id} className={s.feedItem}>
          <div className={s.feedTime}>
            <strong>{activity.createdAt ? getTimeFormats(new Date(activity.createdAt)) : '—'}</strong>
          </div>
          <div className={s.feedBody}>
            <div className={s.feedTop}>
              <span className={s.eventBadge}>{formatEventLabel(activity.eventType)}</span>
              <span className={s.targetBadge}>{formatTargetLabel(activity.targetType)}</span>
            </div>
            <div className={s.feedMember}>
              <span className="material-icons-outlined">person</span>
              {formatUserLabel(activity.userDetails, activity.fkUserId)}
              {activity.userDetails?.userRefId ? (
                <span className={s.muted}> · BCN {activity.userDetails.userRefId}</span>
              ) : null}
            </div>
            {activity.targetId ? <div className={s.muted}>Target ID: {activity.targetId}</div> : null}
            {activity.sessionId ? <div className={s.muted}>Session: {activity.sessionId}</div> : null}
            <div className={s.metadataChips}>{renderMetadata(activity.metadata)}</div>
          </div>
        </article>
      ))}
    </div>
  )
}

const PaginationControls = ({ pageNo, totalPages, setPageNo }) => (
  <div className={s.pagination}>
    <button
      type="button"
      className={s.secondaryBtn}
      disabled={pageNo <= 1}
      onClick={() => setPageNo(prev => Math.max(prev - 1, 1))}
    >
      <span className="material-icons-outlined">chevron_left</span>
      Previous
    </button>
    <span className={s.pageInfo}>
      Page {pageNo} of {totalPages}
    </span>
    <button
      type="button"
      className={s.secondaryBtn}
      disabled={pageNo >= totalPages}
      onClick={() => setPageNo(prev => Math.min(prev + 1, totalPages))}
    >
      Next
      <span className="material-icons-outlined">chevron_right</span>
    </button>
  </div>
)

const EVENT_LABELS = {
  lead_interaction: 'Lead interested',
  matrimonial_profile_submitted: 'Matrimonial profile submitted',
  job_opening_submitted: 'Job opening submitted',
  resume_submitted: 'Resume submitted',
  donation_recorded: 'Donation recorded',
  donation_payment_success: 'Donation payment success',
  contact_form_submitted: 'Contact form sent',
  matrimonial_landing_view: 'Visited Matrimonial home',
  matrimonial_profiles_page_view: 'Browsed Matrimonial profiles',
  matrimonial_search_performed: 'Searched Matrimonial profiles',
  matrimonial_filters_applied: 'Filtered Matrimonial profiles',
  spirituality_tab_change: 'Switched Spirituality tab',
  spirituality_streams_fetch: 'Viewed Spirituality streams',
  purohit_on_call_fetch: 'Viewed Purohit on Call',
  mandir_directory_viewed: 'Viewed Mandir list',
  page_view: 'Page viewed',
  api_request: 'App activity',
  login_success: 'User logged in',
  logout: 'User logged out',
}

const TARGET_LABELS = {
  matrimonial: 'Matrimonial',
  matrimonial_profile: 'Matrimonial',
  job: 'Jobs',
  resume: 'Resumes',
  business: 'Business Directory',
  spirituality: 'Spirituality',
  spirituality_purohit: 'Purohit on Call',
  spirituality_mandir: 'Mandir',
  mandir_list: 'Mandir Directory',
  lead: 'Lead',
  donation: 'Donations',
  support: 'Support & Contact',
  page: 'Pages',
  api: 'App activity',
}

const humanize = value => {
  if (!value) return '—'
  const safe = String(value).trim()
  if (!safe) return '—'
  const lower = safe.replace(/[^a-zA-Z0-9]+/g, ' ').trim().toLowerCase()
  if (!lower) return safe
  return lower
    .split(' ')
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

function formatEventLabel(eventType) {
  if (!eventType) return '—'
  return EVENT_LABELS[String(eventType).toLowerCase()] || humanize(eventType)
}

function formatTargetLabel(targetType) {
  if (!targetType) return '—'
  return TARGET_LABELS[String(targetType).toLowerCase()] || humanize(targetType)
}

function formatUserLabel(userDetails, fkUserId) {
  if (userDetails?.name) return userDetails.name
  if (userDetails?.firstName || userDetails?.lastName) {
    return `${userDetails.firstName || ''} ${userDetails.lastName || ''}`.trim()
  }
  if (fkUserId) return 'BCN member'
  return 'Anonymous visitor'
}

function renderMetadata(metadata) {
  if (!metadata || (typeof metadata === 'object' && !Object.keys(metadata).length)) {
    return <span className={s.chipMuted}>No extra details</span>
  }

  if (typeof metadata !== 'object') {
    return <span className={s.chip}>{String(metadata)}</span>
  }

  const entries = Object.entries(metadata).slice(0, 4)
  const remaining = Object.entries(metadata).length - entries.length

  return (
    <>
      {entries.map(([key, value]) => (
        <span key={key} className={s.chip}>
          <strong>{humanize(key)}:</strong> {formatMetadataValue(value)}
        </span>
      ))}
      {remaining > 0 ? <span className={s.chipMuted}>+{remaining} more</span> : null}
    </>
  )
}

function formatMetadataValue(value) {
  if (value === null || value === undefined) return '—'
  if (Array.isArray(value)) {
    return value.length ? value.slice(0, 3).join(', ') + (value.length > 3 ? '…' : '') : '—'
  }
  if (typeof value === 'object') {
    return Object.keys(value)
      .slice(0, 3)
      .map(key => `${humanize(key)}: ${formatMetadataValue(value[key])}`)
      .join(', ')
  }
  return String(value)
}
